import { useEffect, useState } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plane, Plus, Download, X, Info, AlertTriangle } from 'lucide-react';
import { VISA_TYPES, visaTypeLabel, VISA_STATUS_VARIANT, VISA_STATUS_LABEL } from '../../lib/visas.js';

const emptyForm = () => ({ visaType: 'exit_reentry', purpose: '', neededBy: '' });

export default function MyVisasPage() {
  const toast = useToast();
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    return employeeApi.listMyVisas()
      .then((res) => setVisas(res.visas || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSaving(true);
    try {
      await employeeApi.submitVisa(form);
      toast.success('Visa request submitted');
      setShowNew(false);
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  const cancel = async (v) => {
    if (!confirm('Cancel this visa request?')) return;
    try {
      await employeeApi.cancelVisa(v.rowKey);
      toast.success('Cancelled');
      await load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Visas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Request an Entry/Exit visa. Once approved, HR issues it and you'll be reminded before it expires.
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setShowNew(true); }}>
          <Plus className="h-4 w-4" /> Request a visa
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>
      ) : visas.length === 0 ? (
        <Card><CardContent className="flex items-start gap-3 py-8">
          <Info className="mt-0.5 h-5 w-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">No visa requests yet</p>
            <p className="text-xs text-muted-foreground mt-1">Use “Request a visa” to submit one for approval.</p>
          </div>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {visas.map((v) => {
            const daysLeft = v.endDate ? Math.ceil((new Date(v.endDate) - new Date()) / 86400000) : null;
            const expiringSoon = v.status === 'fulfilled' && daysLeft !== null && daysLeft <= (v.reminderDays || 30);
            return (
              <Card key={v.rowKey}>
                {expiringSoon && (
                  <div className={`flex items-center gap-2 px-6 py-2 text-xs font-medium ${daysLeft <= 0 ? 'bg-destructive/5 text-destructive' : 'bg-warning/5 text-warning'}`}>
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {daysLeft <= 0 ? `Expired on ${v.endDate}` : `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — consider requesting a renewal`}
                  </div>
                )}
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Plane className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{visaTypeLabel(v.visaType)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {v.purpose && `${v.purpose} · `}
                      {v.status === 'fulfilled' && v.endDate ? `Valid until ${v.endDate}` : (v.neededBy ? `Needed by ${v.neededBy}` : `Submitted ${v.submittedAt ? new Date(v.submittedAt).toLocaleDateString() : ''}`)}
                      {v.visaNumber && ` · #${v.visaNumber}`}
                    </div>
                    {v.status === 'rejected' && v.rejectionReason && (
                      <div className="text-xs text-destructive mt-1">Reason: {v.rejectionReason}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={VISA_STATUS_VARIANT[v.status] || 'default'}>{VISA_STATUS_LABEL[v.status] || v.status}</Badge>
                    {v.visaUrl && (
                      <a href={v.visaUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:shadow-md transition-all">
                        <Download className="h-3.5 w-3.5" /> Visa
                      </a>
                    )}
                    {(v.status === 'pending' || v.status === 'approved') && (
                      <button onClick={() => cancel(v)} title="Cancel request" className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showNew && (
        <Modal isOpen onClose={() => setShowNew(false)} title="Request a visa" width="min(560px, 96vw)">
          <div className="space-y-4">
            <Select label="Visa type" value={form.visaType} onChange={(e) => setForm({ ...form, visaType: e.target.value })}
              options={VISA_TYPES} />
            <Input label="Purpose (optional)" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            <Input label="Needed by (optional)" type="date" value={form.neededBy} onChange={(e) => setForm({ ...form, neededBy: e.target.value })} />
            <p className="text-xs text-muted-foreground">This goes through the standard approval chain. Once approved, HR will issue the visa and enter its validity dates.</p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowNew(false)} disabled={saving}>Cancel</Button>
              <Button onClick={submit} loading={saving} disabled={saving}>Submit request</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
